import React, { useMemo, useState } from "react";
import { employeeMonthTotals, formatMngHours, sumFactHours } from "./mngHours";

function computeRow(user, normaHours) {
  const month = employeeMonthTotals(user.shifts, normaHours);
  const info = {
    hours: month.hours,
    fact: sumFactHours(user.fact_hours),
    lunch: month.lunch,
    shifts: 0,
    weekend: 0,
    sickDays: 0,
    vacations: 0,
    norma: month.norma,
    target: month.target,
    different: month.different,
  };

  for (const shift of user.shifts || []) {
    if (!shift) continue;
    if (Number(shift.hours) > 1) info.shifts += 1;
    if (Number(shift.type_shift_id) === 1) continue;
    if (Number(shift.type_shift_id) === 4) {
      info.weekend += 1;
    } else if (Number(shift.type_shift_id) === 5) {
      info.sickDays += 1;
    } else if (Number(shift.type_shift_id) === 6) {
      info.vacations += 1;
    }
  }
  return {
    id: user.user_id,
    name: `${user.surname ?? ""} ${user.name ?? ""}`.trim(),
    post: user.post_name || "",
    ...info,
  };
}

const ScheduleMNGHours = ({ schedule, normaHours = 165 }) => {
  const [query, setQuery] = useState("");
  const [sortKey, setSortKey] = useState("hours");
  const [sortDir, setSortDir] = useState("asc");

  const rows = useMemo(
    () => (schedule?.table || []).map((user) => computeRow(user, normaHours)),
    [schedule, normaHours],
  );

  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase();
    const list = rows.filter((row) => {
      if (!needle) return true;
      return `${row.name} ${row.post}`.toLowerCase().includes(needle);
    });
    const dir = sortDir === "asc" ? 1 : -1;
    list.sort((a, b) => {
      if (sortKey === "name") return a.name.localeCompare(b.name, "ru") * dir;
      return ((a[sortKey] ?? 0) - (b[sortKey] ?? 0)) * dir || a.name.localeCompare(b.name, "ru");
    });
    return list;
  }, [rows, query, sortKey, sortDir]);

  const stats = useMemo(() => {
    const totalHours = rows.reduce((sum, row) => sum + row.hours, 0);
    const totalFact = rows.reduce((sum, row) => sum + row.fact, 0);
    const totalLunch = rows.reduce((sum, row) => sum + row.lunch, 0);
    const under = rows.filter((row) => row.hours < row.norma).length;
    return {
      people: rows.length,
      totalHours,
      totalFact,
      totalLunch,
      average: rows.length ? Math.round((totalHours / rows.length) * 10) / 10 : 0,
      under,
    };
  }, [rows]);

  const maxHours = Math.max(
    1,
    ...rows.map((row) => Math.max(row.hours, row.target, row.norma)),
  );

  const toggleSort = (key) => {
    if (sortKey === key) {
      setSortDir((dir) => (dir === "asc" ? "desc" : "asc"));
      return;
    }
    setSortKey(key);
    setSortDir("asc");
  };

  const sortMark = (key) => (sortKey === key ? (sortDir === "asc" ? " ↑" : " ↓") : "");

  if (!rows.length) return null;

  return (
    <div className="chk-summary mt-4">
      <div className="chk-summary-head">
        <h4>Часы за месяц</h4>
        <p>
          Полоска — плановые часы с вычетом обедов, чёрточка — норма ({normaHours} ч).
          Факт считается по отметкам Boss Control (тоже с вычетом обеда 4 мин/час).
          Нужно отработать {normaHours} ч плюс обеды. Часы округляются вверх до десятых.
        </p>
      </div>
      <div className="chk-summary-kpis">
        <div className="chk-summary-kpi">
          <span>В графике</span>
          <strong>{stats.people}</strong>
        </div>
        <div className="chk-summary-kpi">
          <span>Всего часов</span>
          <strong>{formatMngHours(stats.totalHours)}</strong>
        </div>
        <div className="chk-summary-kpi">
          <span>Всего факт</span>
          <strong>{formatMngHours(stats.totalFact)}</strong>
        </div>
        <div className="chk-summary-kpi">
          <span>Всего обедов</span>
          <strong>{formatMngHours(stats.totalLunch, 2)}</strong>
        </div>
        <div className="chk-summary-kpi">
          <span>Средние часы</span>
          <strong>{formatMngHours(stats.average)}</strong>
        </div>
        <div className="chk-summary-kpi">
          <span>Норма</span>
          <strong>{normaHours}</strong>
        </div>
        <div className="chk-summary-kpi is-low">
          <span>Ниже нормы</span>
          <strong>{stats.under}</strong>
        </div>
      </div>

      <div className="chk-summary-toolbar">
        <input
          type="search"
          className="form-control form-control-sm"
          placeholder="Поиск по ФИО"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
        />
      </div>

      <div className="table-responsive chk-summary-table-wrap">
        <table className="table table-bordered schedule-chk-table chk-summary-table">
          <thead>
            <tr>
              <th>
                <button type="button" onClick={() => toggleSort("name")}>
                  Сотрудник{sortMark("name")}
                </button>
              </th>
              <th>
                <button type="button" onClick={() => toggleSort("hours")}>
                  Часы{sortMark("hours")}
                </button>
              </th>
              <th className="text-center">
                <button type="button" onClick={() => toggleSort("fact")}>
                  Факт{sortMark("fact")}
                </button>
              </th>
              <th className="text-center">
                <button type="button" onClick={() => toggleSort("lunch")}>
                  Обеды{sortMark("lunch")}
                </button>
              </th>
              <th className="text-center">
                <button type="button" onClick={() => toggleSort("shifts")}>
                  Смены{sortMark("shifts")}
                </button>
              </th>
              <th className="text-center">Вых.</th>
              <th className="text-center">Бол.</th>
              <th className="text-center">Отп.</th>
              <th className="text-center">
                <button type="button" onClick={() => toggleSort("norma")}>
                  Норма{sortMark("norma")}
                </button>
              </th>
              <th className="text-center">
                <button type="button" onClick={() => toggleSort("target")}>
                  Норма+обеды{sortMark("target")}
                </button>
              </th>
              <th className="text-center">
                <button type="button" onClick={() => toggleSort("different")}>
                  Откл.{sortMark("different")}
                </button>
              </th>
            </tr>
          </thead>
          <tbody>
            {filtered.length ? (
              filtered.map((row) => {
                const status =
                  row.hours < row.norma ? "low" : row.hours > row.norma ? "high" : "ok";
                return (
                  <tr key={row.id} className={`chk-summary-row is-${status}`}>
                    <td className="chk-summary-employee">
                      <div className="chk-summary-name">{row.name}</div>
                      {row.post ? <div className="chk-summary-post">{row.post}</div> : null}
                    </td>
                    <td>
                      <div className="chk-summary-hours">
                        <div className="chk-summary-hours-bar schedule-mng-hours-bar">
                          <span
                            style={{
                              width: `${Math.min(100, (row.hours / maxHours) * 100)}%`,
                            }}
                          />
                          <i
                            className="schedule-mng-hours-norma"
                            style={{
                              left: `${Math.min(100, (row.norma / maxHours) * 100)}%`,
                            }}
                            title={`Норма ${formatMngHours(row.norma)}`}
                          />
                        </div>
                        <strong>{formatMngHours(row.hours)}</strong>
                      </div>
                    </td>
                    <td className="text-center">{formatMngHours(row.fact)}</td>
                    <td className="text-center">{formatMngHours(row.lunch, 2)}</td>
                    <td className="text-center">{row.shifts}</td>
                    <td className="text-center">{row.weekend}</td>
                    <td className="text-center">{row.sickDays}</td>
                    <td className="text-center">{row.vacations}</td>
                    <td className="text-center">{formatMngHours(row.norma)}</td>
                    <td className="text-center">{formatMngHours(row.target, 2)}</td>
                    <td className="text-center">
                      <span className={`chk-summary-badge is-${status}`}>
                        {row.different > 0
                          ? `+${formatMngHours(row.different)}`
                          : formatMngHours(row.different)}
                      </span>
                    </td>
                  </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan={11} className="chk-summary-empty">
                  Никого не найдено
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ScheduleMNGHours;
